import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getKollaSyncStatus,
  triggerKollaSync,
} from '../services/paymentsService.js';
import { parseBackendDate } from '../services/common.js';

/**
 * Tiny "Last synced X ago" badge + "Sync now" button for the dashboard
 * sidebar. Backed by:
 *   - GET  /api/payments/practices/<id>/kolla-sync-status   (every 30s)
 *   - POST /api/payments/practices/<id>/kolla-sync-now      (button click)
 *
 * Lifecycle:
 *   1. Mounts → fetches status. Renders nothing while ``configured`` is
 *      undefined (first paint) or false (non-Kolla practices), so
 *      practices on other PMSes don't see a confusing widget.
 *   2. Polls every 30s in steady state.
 *   3. After "Sync now" click, polls every 3s for up to 60s waiting for
 *      ``lastAttemptedAt`` to advance past the click-time baseline,
 *      then drops back to the 30s cadence.
 *   4. Disables the button for 5s post-click as a client-side debounce —
 *      the backend doesn't reject duplicates so this is purely about
 *      preventing double-fires from impatient clicks.
 */

const POLL_INTERVAL_STEADY_MS = 30_000;
const POLL_INTERVAL_AFTER_CLICK_MS = 3_000;
const MAX_AFTER_CLICK_POLL_MS = 60_000;
const BUTTON_DEBOUNCE_MS = 5_000;
const RELATIVE_TIME_TICK_MS = 30_000;

function formatRelative(date, now) {
  if (!date) return null;
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) return 'just now'; // clock skew between client + server
  const sec = Math.floor(diffMs / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return date.toLocaleDateString();
}

const KollaSyncIndicator = ({ practiceId }) => {
  const [status, setStatus] = useState(null); // backend response or null
  const [now, setNow] = useState(new Date());
  const [queueState, setQueueState] = useState('idle'); // 'idle' | 'queueing' | 'queued' | 'error'
  const [lastClickError, setLastClickError] = useState(null);

  // Refs hold mutable state the polling loop reads without forcing
  // re-renders when they change.
  const buttonDisabledUntilRef = useRef(0);
  const afterClickDeadlineRef = useRef(0);
  const baselineAttemptedAtRef = useRef(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);
  // Holds the effect's ``scheduleNext`` so ``handleSyncNow`` can restart the
  // self-rescheduling poll loop after its immediate one-shot poll (otherwise
  // the loop dies and the button sticks on "Syncing…").
  const scheduleNextRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    if (!practiceId) return;
    try {
      const data = await getKollaSyncStatus(practiceId);
      if (!mountedRef.current) return;
      if (data && data.success) {
        setStatus(data);

        // If the backend's ``lastAttemptedAt`` has moved past the
        // baseline we captured at click time, the queued task landed.
        // Drop back to steady-state polling.
        if (
          afterClickDeadlineRef.current &&
          baselineAttemptedAtRef.current &&
          data.lastAttemptedAt &&
          data.lastAttemptedAt > baselineAttemptedAtRef.current
        ) {
          afterClickDeadlineRef.current = 0;
          baselineAttemptedAtRef.current = null;
          setQueueState('idle');
        }
      }
    } catch (err) {
      console.error('KollaSyncIndicator: fetch failed', err);
    }
  }, [practiceId]);

  // Polling loop — self-rescheduling setTimeout so the cadence can
  // change between ticks without restarting the whole effect.
  useEffect(() => {
    mountedRef.current = true;
    const scheduleNext = () => {
      if (!mountedRef.current) return;

      // Drop the after-click state if we hit the 60s ceiling without
      // seeing the timestamp advance (worker / broker hiccup).
      if (
        afterClickDeadlineRef.current &&
        Date.now() >= afterClickDeadlineRef.current
      ) {
        afterClickDeadlineRef.current = 0;
        baselineAttemptedAtRef.current = null;
        setQueueState((s) => (s === 'queued' ? 'idle' : s));
      }

      const inAfterClickWindow =
        afterClickDeadlineRef.current && Date.now() < afterClickDeadlineRef.current;
      const period = inAfterClickWindow
        ? POLL_INTERVAL_AFTER_CLICK_MS
        : POLL_INTERVAL_STEADY_MS;

      pollTimerRef.current = setTimeout(async () => {
        await fetchStatus();
        scheduleNext();
      }, period);
    };
    // Expose to handleSyncNow so it can restart the loop after its one-shot.
    scheduleNextRef.current = scheduleNext;

    fetchStatus().then(scheduleNext);

    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, [fetchStatus]);

  // Tick the relative-time clock so "2m ago" rolls to "3m ago" without
  // waiting for the next status poll.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), RELATIVE_TIME_TICK_MS);
    return () => clearInterval(t);
  }, []);

  const handleSyncNow = async () => {
    if (Date.now() < buttonDisabledUntilRef.current) return;
    if (!practiceId) return;

    buttonDisabledUntilRef.current = Date.now() + BUTTON_DEBOUNCE_MS;
    setLastClickError(null);
    setQueueState('queueing');

    // Capture current ``lastAttemptedAt`` so the polling loop knows
    // when the freshly-queued task lands (timestamp moves past this).
    baselineAttemptedAtRef.current = status?.lastAttemptedAt || '';
    afterClickDeadlineRef.current = Date.now() + MAX_AFTER_CLICK_POLL_MS;

    try {
      const resp = await triggerKollaSync(practiceId);
      if (!mountedRef.current) return;
      if (resp && resp.success) {
        setQueueState('queued');
        // Trigger an immediate poll so the spinner reflects progress without
        // waiting for the 3s scheduled tick — then re-chain scheduleNext so
        // the fast-cadence loop (and its 60s ceiling reset) keeps running.
        // Without the re-chain the loop dies and the button sticks on
        // "Syncing…" if lastAttemptedAt hasn't advanced by the one-shot.
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        pollTimerRef.current = setTimeout(async () => {
          await fetchStatus();
          if (scheduleNextRef.current) scheduleNextRef.current();
        }, 1_000);
      } else {
        setQueueState('error');
        setLastClickError(resp?.error || 'Failed to queue sync task');
        afterClickDeadlineRef.current = 0;
        baselineAttemptedAtRef.current = null;
      }
    } catch (err) {
      if (!mountedRef.current) return;
      setQueueState('error');
      setLastClickError(err?.message || 'Network error');
      afterClickDeadlineRef.current = 0;
      baselineAttemptedAtRef.current = null;
    }
  };

  if (!status) return null;
  if (status.configured === false) return null;

  const succeededDate = parseBackendDate(status.lastSucceededAt);
  const attemptedDate = parseBackendDate(status.lastAttemptedAt);
  const hasFailedSinceLastSuccess =
    !!status.lastError &&
    attemptedDate &&
    succeededDate &&
    attemptedDate > succeededDate;

  // Headline timestamp:
  //  - prefer ``lastSucceededAt`` (actual freshness of the data).
  //  - fall back to ``lastAttemptedAt`` if we have never succeeded.
  const headlineDate = succeededDate || attemptedDate;
  const relative = formatRelative(headlineDate, now);
  const tooltip = headlineDate
    ? headlineDate.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      })
    : '';

  const buttonDisabled =
    queueState === 'queueing' ||
    queueState === 'queued' ||
    Date.now() < buttonDisabledUntilRef.current;

  let buttonLabel = 'Sync now';
  if (queueState === 'queueing') buttonLabel = 'Queueing…';
  else if (queueState === 'queued') buttonLabel = 'Syncing…';

  return (
    <div
      style={{
        marginTop: '12px',
        padding: '10px 12px',
        background: '#f7f8f7',
        border: '1px solid #eef0ee',
        borderRadius: '8px',
        fontSize: '0.78rem',
        color: '#3c4540',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '6px',
        }}
      >
        <span style={{ fontWeight: 600, color: '#1a1d1b' }}>PMS sync</span>
        {hasFailedSinceLastSuccess && (
          <span
            title={status.lastError}
            style={{ color: '#b42318', fontSize: '0.72rem' }}
          >
            ⚠ last attempt failed
          </span>
        )}
      </div>

      <div title={tooltip} style={{ marginBottom: '8px' }}>
        {headlineDate ? (
          <>
            Last synced{' '}
            <span style={{ color: '#1a1d1b', fontWeight: 500 }}>{relative}</span>
          </>
        ) : (
          <span style={{ color: '#6b716d', fontStyle: 'italic' }}>
            Never synced
          </span>
        )}
      </div>

      <button
        onClick={handleSyncNow}
        disabled={buttonDisabled}
        style={{
          width: '100%',
          padding: '6px 10px',
          background: buttonDisabled ? '#eef0ee' : '#1f5a3d',
          color: buttonDisabled ? '#6b716d' : '#ffffff',
          border: 'none',
          borderRadius: '6px',
          fontSize: '0.78rem',
          fontWeight: 600,
          cursor: buttonDisabled ? 'not-allowed' : 'pointer',
          transition: 'background 0.15s ease',
        }}
      >
        {buttonLabel}
      </button>

      {lastClickError && (
        <div
          style={{
            marginTop: '6px',
            color: '#b42318',
            fontSize: '0.72rem',
          }}
        >
          {lastClickError}
        </div>
      )}
    </div>
  );
};

export default KollaSyncIndicator;
